import React from 'react';
import { StyledLogo, StyledLogoText } from './Logo.styled';

export type LogoSize = 'sm' | 'md' | 'lg';

export interface LogoProps {
  /**
   * Size variant
   */
  size?: LogoSize;
  /**
   * Show text alongside logo
   */
  showText?: boolean;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * Logo component following planit.family design system
 *
 * Uses brand colors: Gold (#F5A623), Navy (#2C4A6B), Terracotta (#C86B3E), Teal (#6ABFAB)
 *
 * @example
 * <Logo size="lg" showText />
 * <Logo size="md" />
 */
export const Logo: React.FC<LogoProps> = ({
  size = 'md',
  showText = false,
  className,
}) => {
  return (
    <StyledLogo className={className} $size={size}>
      <svg
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="planit.family logo"
      >
        {/* Calendar icon base - Navy */}
        <rect
          x="8"
          y="12"
          width="32"
          height="28"
          rx="4"
          fill="#2C4A6B"
        />
        {/* Calendar header - Gold */}
        <rect
          x="8"
          y="12"
          width="32"
          height="8"
          rx="4"
          fill="#F5A623"
        />
        {/* Calendar ring holes */}
        <circle cx="16" cy="16" r="2" fill="#2C4A6B" />
        <circle cx="32" cy="16" r="2" fill="#2C4A6B" />
        {/* Calendar grid - Teal accents */}
        <rect x="12" y="24" width="4" height="4" rx="1" fill="#6ABFAB" />
        <rect x="18" y="24" width="4" height="4" rx="1" fill="#6ABFAB" />
        <rect x="24" y="24" width="4" height="4" rx="1" fill="#6ABFAB" />
        <rect x="30" y="24" width="4" height="4" rx="1" fill="#6ABFAB" />
        {/* Heart accent - Terracotta */}
        <path
          d="M24 34C24 34 16 29 16 25C16 23 17 22 18.5 22C20 22 22 23 24 25C26 23 28 22 29.5 22C31 22 32 23 32 25C32 29 24 34 24 34Z"
          fill="#C86B3E"
        />
      </svg>
      {showText && <StyledLogoText>planit.family</StyledLogoText>}
    </StyledLogo>
  );
};

Logo.displayName = 'Logo';
